<script>
    import * as Icons from 'lucide-svelte';
    import LyP from '../../layouts/components/LyP.svelte';
    import LyHeader from '../../layouts/components/LyHeader.svelte';
    import LySection from '../../layouts/components/LySection.svelte';
    import LyContent from '../../layouts/components/LyContent.svelte';
    import LyHeadline from '../../layouts/components/LyHeadline.svelte';
    import AiAnnouncementList from './announcement/AiAnnouncementList.svelte';
    import AiAnnouncementCreate from './announcement/AiAnnouncementCreate.svelte';
    import LyButton from '../../layouts/forms/LyButton.svelte';
    import LyButtonLine from '../../layouts/forms/LyButtonLine.svelte';
    import AiAnnouncementUsers from './announcement/AiAnnouncementUsers.svelte';
    import { UserStore } from '../../stores/UserStore.svelte.js';
    import { addWorksheet, createWorkbook } from '@office-kit/xlsx/workbook';
    import { setCell, setColumnWidth } from '@office-kit/xlsx/worksheet';
    import { makeAlignment, makeFont, makePatternFill, rgbColor, setCellStyle } from '@office-kit/xlsx/styles';
    import { workbookToBytes } from '@office-kit/xlsx/io';
    import { ValueService } from '../../services/ValueService.js';
    import { DownloadService } from '../../services/DownloadService.js';
    import { AiAnnouncementStore } from './announcement/AiAnnouncementStore.svelte.js';
    import { UsersStore } from '../../stores/UsersStore.svelte.js';
    import {
        humanFullOversightOptions,
        riskFullOptions,
        roleFullOptions,
    } from './announcement/aiAnnouncementOptions.js';

    /** @type {'' | 'create' | 'users'} */
    let mode = $state('');
    let isAdmin = $state(UserStore.hasGroup('AI'));

    const headerStyle = {
        font: makeFont({
            name: 'Arial',
            size: 10,
            bold: true,
            color: rgbColor('FF333333'),
        }),
        fill: makePatternFill({
            patternType: 'solid',
            fgColor: rgbColor('FFDDDDDD'),
        }),
        alignment: makeAlignment({
            horizontal: 'left',
        }),
    };
    const bodyStyle = {
        font: makeFont({
            name: 'Arial',
            size: 12,
            color: rgbColor('FF000000'),
        }),
        alignment: makeAlignment({
            horizontal: 'left',
        }),
    };
    const bodyStyleEven = {
        ...bodyStyle,
        fill: makePatternFill({
            patternType: 'solid',
            fgColor: rgbColor('FFEEEEEE'),
        }),
    };
    const bodyStyleOdd = {
        ...bodyStyle,
        fill: makePatternFill({
            patternType: 'solid',
            fgColor: rgbColor('FFFFFFFF'),
        }),
    };

    async function generateExcel() {
        const excelFile = createWorkbook();
        const excelSheet = addWorksheet(excelFile, 'AiAnnouncement');

        /**
         * @param {number} colNumber
         * @param {string} title
         * @param {number} width
         */
        const createHeaderCol = (colNumber, title, width) => {
            const headerCol = setCell(excelSheet, 1, colNumber, title);
            setCellStyle(excelFile, headerCol, headerStyle);
            setColumnWidth(excelSheet, colNumber, width);
            return headerCol;
        };

        /**
         * @param {number} rowNumber
         * @param {number} colNumber
         * @param {string} content
         */
        const createBodyCol = (rowNumber, colNumber, content) => {
            const bodyCol = setCell(excelSheet, rowNumber, colNumber, content);
            setCellStyle(excelFile, bodyCol, rowNumber % 2 === 0 ? bodyStyleEven : bodyStyleOdd);
            return bodyCol;
        };
        /**
         * @typedef {{
         *     key: string,
         *     title: string,
         *     width: number
         * }} ExcelColumn
         */
        /** @type {ExcelColumn[]} */
        const columns = [
            { key: 'ai_announcement_id', title: 'ID', width: 8 },
            { key: 'user', title: 'Benutzer', width: 15 },
            { key: 'firstName', title: 'Vorname', width: 20 },
            { key: 'lastName', title: 'Nachname', width: 20 },
            { key: 'company', title: 'Anbieter', width: 25 },
            { key: 'system', title: 'System', width: 30 },
            { key: 'used_for', title: 'Einsatzbereich', width: 50 },
            { key: 'used_since', title: 'Genutzt seit', width: 15 },
            { key: 'jobs', title: 'Funktion', width: 50 },
            { key: 'data_type', title: 'Datenarten', width: 50 },
            { key: 'is_pii', title: 'Personenbezug', width: 15 },
            { key: 'risk', title: 'Risikoklasse', width: 25 },
            { key: 'role', title: 'Rolle', width: 25 },
            { key: 'human_oversight', title: 'Human Oversight', width: 25 },
            { key: 'is_art4', title: 'Art. 4 AI Literacy', width: 20 },
            { key: 'is_art50', title: 'Art. 50 Transparenz', width: 20 },
            { key: 'is_art14', title: 'Art. 14 Oversight', width: 20 },
            { key: 'is_art27', title: 'Art. 27 FRIA', width: 20 },
            { key: 'is_dsfa', title: 'DSFA', width: 20 },
            { key: 'is_avv', title: 'AVV', width: 20 },
            { key: 'is_eu_hosting', title: 'EU Hosting', width: 20 },
            { key: 'todo_message', title: 'Maßnahmen', width: 30 },
            { key: 'todo_name', title: 'Verantwortlich', width: 25 },
            { key: 'todo_till', title: 'Deadline', width: 15 },
            { key: 'created_at', title: 'Erstellt', width: 25 },
            { key: 'updated_at', title: 'Geändert', width: 25 },
        ];

        // -
        columns.forEach((column, index) => {
            createHeaderCol(index + 1, column.title, column.width);
        });

        // - Body
        let rowNumber = 1;
        await UsersStore.reset();
        const usersById = new Map(UsersStore.users.map((user) => [user.userId, user]));

        /**
         * @param {string} value
         * @param {{value:string,label:string}[]} options
         * @returns {string}
         */
        function getOptionLabel(value, options) {
            return options.find((option) => option.value === value)?.label ?? '-' + value + '-';
        }

        AiAnnouncementStore.list.forEach((item) => {
            ++rowNumber;
            const user = usersById.get(item.user_id);

            /** @type {Record<string, string|number|boolean|null>} */
            const values = {
                ai_announcement_id: item.ai_announcement_id,
                user: user?.userAcronym ?? '',
                firstName: user?.firstName ?? '',
                lastName: user?.lastName ?? '',
                company: item.company,
                system: item.system,
                used_for: item.used_for,
                used_since: ValueService.formatDate(item.used_since),
                jobs: item.jobs,
                data_type: item.data_type,
                is_pii: item.is_pii ? 'Ja' : 'Nein',
                risk: getOptionLabel(item.risk ?? '', riskFullOptions),
                role: getOptionLabel(item.role ?? '', roleFullOptions),
                human_oversight: getOptionLabel(item.human_oversight ?? '', humanFullOversightOptions),
                is_art4: item.is_art4 ? 'Ja' : 'Nein',
                is_art50: item.is_art50 ? 'Ja' : 'Nein',
                is_art14: item.is_art14 ? 'Ja' : 'Nein',
                is_art27: item.is_art27 ? 'Ja' : 'Nein',
                is_dsfa: item.is_dsfa ? 'Ja' : 'Nein',
                is_avv: item.is_avv ? 'Ja' : 'Nein',
                is_eu_hosting: item.is_eu_hosting ? 'Ja' : 'Nein',
                todo_message: item.todo_message,
                todo_name: item.todo_name,
                todo_till: ValueService.formatDate(item.todo_till),
                created_at: ValueService.formatDate(item.created_at, 'd.m.Y, H:i Uhr'),
                updated_at: ValueService.formatDate(item.updated_at, 'd.m.Y, H:i Uhr'),
            };

            columns.forEach((column, index) => {
                createBodyCol(rowNumber, index + 1, String(values[column.key] ?? ''));
            });
        });

        // -
        const excelBytes = await workbookToBytes(excelFile);
        DownloadService.downloadBytes(
            excelBytes,
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            ValueService.getNowPrefix() + '_AiAnnouncement.xlsx'
        );
    }
</script>

<LyContent wrapper={true}>
    <LyHeader>
        <LyHeadline icon={Icons.ToolCase}>AI-Inventur</LyHeadline>
    </LyHeader>

    <LySection tag="div">
        <LyP>
            Pro AI-System nutzt Du bitte ein eigenes Arbeitsblatt der AI-Inventur. Die Felder bei denen Du Dir unsicher
            bist, trägst Du bitte Deine Vermutung ein.
        </LyP>
    </LySection>
</LyContent>

<LySection tag="div" mt={true}>
    {#if mode === 'create'}
        <AiAnnouncementCreate cancelButton={() => (mode = '')} />
    {:else if isAdmin && mode === 'users'}
        <AiAnnouncementUsers backButton={() => (mode = '')} />
    {:else}
        <LyButtonLine mb={true}>
            <LyButton label="Neues Arbeitsblatt erstellen" onclick={() => (mode = 'create')} />
            {#if isAdmin}
                <LyButton color={true} label="Benutzer-Übersicht" onclick={() => (mode = 'users')} />
            {/if}
            <LyButton color={true} label="Excel-Export" onclick={generateExcel} />
        </LyButtonLine>

        <AiAnnouncementList withUser={isAdmin} />
    {/if}
</LySection>
